import React, { useEffect, useState } from 'react'
import { format } from 'date-fns'
import { useTranslate } from '@helpers'
import { RankmiTable } from '@rankmi/rankmi-table'
import { faBook } from '@fortawesome/pro-solid-svg-icons/faBook'
import { userProfileService } from '@services'
import Profiles from 'modules/reusables/profiles'
import Advance from 'modules/reusables/advance'
import CourseType from 'modules/reusables/courseType'
import MandatoryCourse from 'modules/reusables/mandatoryCourse'
import { IProfile } from 'interfaces/profiles'
import { ICourse } from 'interfaces/course'
import { Wrapper, Description, RankmiTableWrapper, CourseName } from './styled'
import CourseApproval from 'modules/reusables/courseApproval'
import ExternalCourseIndicator from 'modules/reusables/externalCourseIndicator'
import EmptyState from 'modules/reusables/emptyState'
import { IconProp } from '@fortawesome/fontawesome-svg-core'

interface IProps {
  userId: number
}

export const Courses = ({ userId }: IProps) => {
  const { t } = useTranslate()
  const [courses, setCourses] = useState<ICourse[]>([])
  const [totalCourses, setTotalCourses] = useState(0)
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [searchText, setSearchText] = useState('')
  const [order, setOrder] = useState('asc')
  const [fieldToOrder, setFieldToOrder] = useState('name')
  const prefix = 'configuration.userProfile.tabs.courses'

  useEffect(() => {
    getCourses(currentPage, searchText, fieldToOrder, order)
  }, [])

  const getCourses = (
    page: number,
    search: string,
    orderBy: string,
    orderType: string
  ) => {
    setLoading(true)
    userProfileService
      .getMyCourses(userId, page, 10, search, orderBy, orderType)
      .then((response) => {
        setCourses(response.data)
        setTotalCourses(response.total)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const onSearch = (value: string) => {
    setCurrentPage(1)
    setSearchText(value)
    getCourses(1, value, fieldToOrder, order)
  }

  const onSortChange = (value: { order: string; field: string }) => {
    let newOrder = ''
    let newField = ''
    switch (value.order) {
      case 'ascend':
        newOrder = 'asc'
        break
      case 'descend':
        newOrder = 'desc'
        break
      default:
        newOrder = 'asc'
        break
    }
    switch (value.field) {
      case 'type':
        newField = 'course_subtype'
        break
      default:
        newField = value.field
        break
    }
    setOrder(newOrder)
    setFieldToOrder(newField)
    getCourses(currentPage, searchText, newField, newOrder)
  }

  const onPaginationChange = (value: number) => {
    setCurrentPage(value)
    getCourses(value, searchText, fieldToOrder, order)
  }

  const locale = {
    header: {
      rowsCount: t(`${prefix}.rowsCount`),
      rowsCount_plural: t(`${prefix}.rowsCountPlural`)
    }
  }

  const columns = [
    {
      title: t(`${prefix}.name`),
      dataIndex: 'name',
      key: 'name',
      hideable: false,
      sorteable: true,
      render: (name: string, data: ICourse) => (
        <CourseName>
          <span>{name}</span>
          {data.integration_course && (
            <ExternalCourseIndicator
              provider={data.integration_course.provider}
            />
          )}
        </CourseName>
      )
    },
    {
      title: t(`${prefix}.profiles`),
      dataIndex: 'profiles',
      key: 'profiles',
      width: 216,
      render: (profiles: IProfile[]) => <Profiles items={profiles} />
    },
    {
      title: t(`${prefix}.mandatory.title`),
      sorteable: true,
      dataIndex: 'mandatory',
      key: 'mandatory',
      width: 160,
      render: (mandatory: boolean) => <MandatoryCourse value={mandatory} />
    },
    {
      title: t(`${prefix}.type`),
      sorteable: true,
      dataIndex: 'type',
      key: 'type',
      width: 160,
      render: (type: number) => <CourseType value={type} />
    },
    {
      title: t(`${prefix}.advance`),
      sorteable: true,
      dataIndex: 'overall_progress',
      key: 'overall_progress',
      width: 200,
      render: (overallProgress: number, data: ICourse) => (
        <Advance value={data.advance} overallProgress={overallProgress} />
      )
    },
    {
      title: t(`${prefix}.approvalStatus`),
      sorteable: true,
      dataIndex: 'approved',
      key: 'approved',
      width: 174,
      render: (approved: boolean, data: ICourse) => (
        <CourseApproval value={approved} advance={data.advance} />
      )
    },
    {
      title: t(`${prefix}.period`),
      sorteable: true,
      dataIndex: 'enrol_finish_date',
      key: 'enrol_finish_date',
      width: 180,
      render: (enrolFinishDate: string, data: ICourse) => (
        <div>
          {data.advance === 2
            ? `${format(
                new Date(data.enrol_start_date),
                'dd/MM/yyyy'
              )} - ${format(new Date(enrolFinishDate), 'dd/MM/yyyy')}`
            : '--'}
        </div>
      )
    }
  ]

  return (
    <Wrapper>
      {totalCourses || loading || searchText ? (
        <div>
          <Description>{t(`${prefix}.header.description`)}</Description>
          <RankmiTableWrapper>
            <RankmiTable
              columnHeaders={columns}
              data={courses}
              countIcon={faBook}
              search={searchText}
              onSearch={onSearch}
              total={totalCourses}
              locale={locale}
              changeColumnsVisibility
              loading={loading}
              currentPage={currentPage}
              onPaginationChange={onPaginationChange}
              initialRowsPerPage={10}
              onSortChange={onSortChange}
              showScrollButtons
              customEmpty={
                loading ? (
                  <div></div>
                ) : (
                  <EmptyState
                    title={t(`${prefix}.emptyState.noMatches`)}
                    icon={faBook as IconProp}
                  />
                )
              }
              delaySearch
              responsive
            />
          </RankmiTableWrapper>
        </div>
      ) : (
        <EmptyState
          title={t(`${prefix}.emptyState.noCourses`)}
          icon={faBook as IconProp}
        />
      )}
    </Wrapper>
  )
}

export default Courses
